import { useAccountStore } from "@/_store/account.module";
import {
  addonsToModuleMap,
  bundlesForAddons,
  bundlesForModules,
  CoroBanner,
  defaultSubscriptionModulesConfig,
  SubscriptionAddon,
  SubscriptionModule,
  SubscriptionType,
  WorkspaceLocale,
} from "@/constants/workplaces";
import { defaultDialogsConfig, type DialogConfig, useDialogsStore } from "@/_store/dialogs.module";

import defaultsDeep from "lodash/defaultsDeep";
import cloneDeep from "lodash/cloneDeep";
import uniqBy from "lodash/uniqBy";
import { ModalWidth } from "@/constants/modals";
import { i18n } from "@/plugins/i18n";
import type {
  ComponentDialogConfig,
  ConfirmationDialogConfig,
  ListDialogConfig,
  OpenSearchCursor,
  OpenSearchQuery,
  Pagination,
  TimeFrame,
} from "@/types";
import type { ISelection } from "@/_store/selector.module";
import {
  type Component,
  computed,
  markRaw,
  nextTick,
  type Ref,
  toRaw,
  type WritableComputedRef,
} from "vue";
import moment, { type MomentInput } from "moment/moment";
import type { UnixTimeFrame } from "@/_helpers/api.types";
import type { WorkspaceDetails } from "@/_store/workspaces-list.module.ts";
import { RolePermissionAccessMode } from "@/constants/roles";
import { isObject, isArray } from "lodash";
import {
  type NestedWorkspacePermissions,
  RolePermissionsScope,
  type WorkspaceRolePermissions,
} from "@/_store/roles.module";
import { OsType } from "@/constants/devices";
import type { DeviceVersionItem } from "@/_store/devices-settings.module.ts";
import type { AxiosRequestConfig } from "axios";
import { Service, ServiceAction, ServiceStatus } from "@/constants/cloud-apps";
import get from "lodash/get";
import api from "@/_helpers/api";
import { countriesList, usaStatesList } from "@/constants/countries";
import { axiosInstance } from "@/plugins/https";
import isEmpty from "lodash/isEmpty";
import { TicketAction, TicketActionSeverity, TicketType } from "@/constants/tickets";
import ListModal from "@/components/modals/network/ListModal.vue";
import { type Subscription, useMspStore } from "@/_store/msp/msp.module.ts";
import { useMyAccountStore } from "@/_store/my-account.module.ts";
import { useFiltersStore } from "@/_store/filters.module.ts";
import { useAiAssistantStore } from "@/_store/ai-assistant.module.ts";
import sortBy from "lodash/sortBy";
import unionBy from "lodash/unionBy";
import { getActivePinia, type Pinia, type Store } from "pinia";
import intersection from "lodash/intersection";
import { firstPageQuery, lastPageQuery } from "@/constants/general.ts";
import { DemoWillStartModal } from "@/constants/tutorial.ts";
import { RouteName } from "@/constants/routes.ts";
import router from "@/_helpers/router.ts";
import { type LocationQuery, type LocationQueryRaw } from "vue-router";
import { type CloudAppService, useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";
import { useGtm } from "@gtm-support/vue-gtm";
import { StoreModule } from "@/constants/store.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { usePermissionsStore, type UserPermissionsResponse } from "@/_store/permissions.module.ts";
import {
  type ModulesSettings,
  type SubscriptionAddonInfo,
  type SubscriptionModuleInfo,
  useWorkspaceStore,
} from "@/_store/workspace.module.ts";
import { GlobalPolicySection } from "@/constants/msp.ts";
import {
  type Finding,
  FindingDetectedInType,
  type TicketActionWithSeverity,
  type TicketDetails,
} from "@/_store/tickets/tickets.module.ts";
import escape from "lodash/escape";
import type { DeviceOsOrClientVersions } from "@/_store/devices.module.ts";
import type { SetupHubStepItem } from "@/_store/setup-hub.module.ts";
import { SetupHubStep } from "@/constants/setup-hub.ts";
import { openConnectCloudAppsModal } from "@/_helpers/dialog-helpers";
import { getCloudSecurityContext } from "@/_helpers/cloud-security.ts";

interface ExtendedPinia extends Pinia {
  _s: Map<string, Store>;
}

interface UseResetStore {
  all: () => void;
  allExceptIgnoreList: (ignoreList?: StoreModule[]) => void;
  [key: string]: () => void;
}

/**
 * Returns true if the provided parameter is the valid JSON.
 * @param item
 */
export const isJson = (item: any): boolean => {
  item = typeof item !== "string" ? JSON.stringify(item) : item;

  try {
    item = JSON.parse(item);
  } catch {
    return false;
  }

  return typeof item === "object" && item !== null;
};

/**
 * Parses JWT token
 * @param token
 * @returns {null|any}
 */
export const parseJwt = (token: string | null) => {
  try {
    if (!token) return;
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

/**
 * Helper to handle virtual (aka infinite) scroll.
 * Merges old data with new data which arrived if we reached the end of scroll.
 * @param currentData
 * @param newData
 * @param id
 * @returns {Array|*}
 */
export const handleVirtualScrollData = <T>(
  currentData: Array<T>,
  newData: Array<T>,
  id: keyof T
): T[] => {
  if (currentData) {
    return uniqBy([...currentData, ...newData], id);
  }
  return newData;
};

export const generateDeviceId = () => {
  return `${window.crypto.getRandomValues(new Uint32Array(1))[0]}${+new Date().getTime()}`;
};

/**
 * Extracts Coro subdomain except for reserved subdomains (qa,dev,prod,beta,local).
 * @param hostname
 * @returns {null|string}
 */
export const extractCoroSubdomain = (hostname: string): null | string => {
  const subdomain = hostname.split(".coro.net")[0];
  const reservedSubdomains = [
    "bugatti-qa",
    "bugatti-dev",
    "secure",
    "beta",
    "localhost",
    "dev2",
    "major-dev-1",
    "major-dev-2",
    "minor-dev",
    "major-qa",
    "minor-qa",
    "hotfix-dev-qa",
    "major-beta",
    "minor-beta",
    "hotfix-beta",
  ];
  return reservedSubdomains.includes(subdomain) ? null : subdomain;
};

export const handleFaviconAndTitle = (subdomain: string) => {
  const subdomainsWithCustomFaviconAndTitle = ["domain-test", "keplersafe"];
  const domainsToDispayName: Record<string, string> = {
    keplersafe: "Kepler Safe",
    "domain-test": "Domain Test",
  };
  if (subdomainsWithCustomFaviconAndTitle.includes(subdomain)) {
    const favicon16 = document.getElementById("favicon-16") as HTMLLinkElement;
    const favicon32 = document.getElementById("favicon-16") as HTMLLinkElement;
    const faviconShortcut = document.getElementById("fav-shortcut") as HTMLLinkElement;
    const title = document.getElementById("browser-tab-title") as HTMLTitleElement;
    const favManifest = document.getElementById("fav-manifest") as HTMLLinkElement;
    const favBrowserConfig = document.getElementById("fav-browserconfig") as HTMLMetaElement;
    const awsUrl = "https://coro-favicon.s3.amazonaws.com";
    favicon16.href = `${awsUrl}/${subdomain}/favicon-16x16.png`;
    favicon32.href = `${awsUrl}/${subdomain}/favicon-32x32.png`;
    faviconShortcut.href = `${awsUrl}/${subdomain}/favicon.ico`;
    favManifest.href = `${awsUrl}/${subdomain}/site.webmanifest`;
    favBrowserConfig.content = `${awsUrl}/${subdomain}/browserconfig.xml`;
    title.innerText = `${domainsToDispayName[subdomain]} Console`;
  }
};

/**
 * Checks if module is disabled.
 * @param moduleName
 * @returns {boolean}
 */
export const isModuleDisabled = (moduleName: SubscriptionModule): boolean => {
  const workspaceStore = useWorkspaceStore();
  const modules = workspaceStore.workspace.subscription?.modules;
  if (!modules) {
    return true;
  }
  return modules[moduleName] ? !modules[moduleName].enabled : true;
};
/**
 * Checks if addon is disabled by checking if module or addon is disabled.
 * @param addonName
 * @returns {boolean}
 */
export const isAddonDisabled = (addonName: SubscriptionAddon): boolean => {
  const workspaceStore = useWorkspaceStore();
  const modules = workspaceStore.workspace.subscription?.modules;
  if (!modules) {
    return true;
  }
  const addonModuleName = addonsToModuleMap[addonName];
  if (!addonModuleName || !modules[addonModuleName] || isModuleDisabled(addonModuleName)) {
    return true;
  }
  const addon = modules[addonModuleName].addons.find((v) => v.name === addonName);
  return addon ? !addon.enabled : true;
};

/**
 * Generic dialogs config constructor, use it when custom dialog config should be created
 * @param config
 */
export const dialogsConfigConstructor = (config: Partial<DialogConfig>): DialogConfig => {
  const defaultState = cloneDeep(defaultDialogsConfig);
  return defaultsDeep(config, defaultState);
};

/**
 * Creates an array consisting of numbers between start and end
 * @param {number} start - From which value range should start
 * @param {number} stop - Where range ends
 * @returns {number[]}
 * @example
 * createNumberArrayRange(3, 5) // [3,4,5]
 * createNumberArrayRange(5, 9) // [5,6,7,8,9]
 */
export const createNumberArrayRange = (start: number, stop: number) => {
  return Array.from({ length: stop - start + 1 }, (value, index) => start + index);
};

export const arrayToLowerCase = (array: string[]) => {
  return array.map((item) => item?.toLowerCase());
};
/**
 * Dialogs config constructor for confirmation dialogs, covers most of the cases of confirmation dialogs
 * @param item
 * @param action
 * @param callback
 * @param disable
 * @param disclaimer
 * @param width
 * @param text
 * @param title
 */
export const confirmationDialogsConfigConstructor = ({
  item,
  action,
  callback,
  disable,
  disclaimer,
  width,
  text,
  title,
}: ConfirmationDialogConfig): DialogConfig => {
  const dialogConfig = {
    width: width || ModalWidth.SMALL,
    header: {
      title: title || i18n.global.t(`modals.${action}.title`, { ...item }),
      close: true,
    },
    content: {
      html: text || i18n.global.t(`modals.${action}.description`, { ...item }),
    },
    disable: disable ?? isWorkspaceFrozen(),
    disclaimer: getDisclaimerText(disable, disclaimer),
    footer: {
      buttons: [
        {
          title: i18n.global.te(`modals.${action}.cancelBtn`)
            ? i18n.global.t(`modals.${action}.cancelBtn`)
            : i18n.global.t("general.cancel"),
          spacer: "before",
          type: "text",
          cancel: true,
        },
        {
          title: i18n.global.t(`modals.${action}.actionBtn`),
          color: "primary",
          callback: callback,
          cancel: false,
        },
      ],
    },
    data: {
      item,
      action,
    },
  };
  return dialogsConfigConstructor(dialogConfig);
};
/**
 * Generic constructor for dialogs with custom components
 * @param item
 * @param action
 * @param component
 * @param width
 * @param callback
 * @param closeCallback
 * @param disable
 * @param disclaimer
 * @param hideFooter
 * @param scrollable
 * @param customErrorHandling
 * @param showValidationAlert
 * @param title
 */
export const componentDialogsConfigConstructor = <T = any>({
  item,
  action,
  component,
  width,
  callback,
  closeCallback,
  disable,
  disclaimer,
  hideFooter,
  scrollable,
  customErrorHandling,
  showValidationAlert,
  title,
}: ComponentDialogConfig<T>): DialogConfig => {
  const dialogConfig: DialogConfig = {
    width,
    header: {
      title: title || i18n.global.t(`modals.${action}.title`, { ...item }),
      close: true,
    },
    content: { component },
    disable: disable ?? isWorkspaceFrozen(),
    footer: {
      buttons: [
        {
          title: i18n.global.te(`modals.${action}.cancelBtn`)
            ? i18n.global.t(`modals.${action}.cancelBtn`)
            : i18n.global.t("general.cancel"),
          spacer: "before",
          type: "text",
          cancel: true,
        },
        {
          title: i18n.global.t(`modals.${action}.actionBtn`),
          color: "primary",
          callback,
          cancel: false,
        },
      ],
    },
    disclaimer: getDisclaimerText(disable, disclaimer),
    data: {
      item,
      action,
      callback,
    },
    closeCallback,
    scrollable,
    customErrorHandling,
    showValidationAlert,
  };

  if (hideFooter) {
    dialogConfig.footer = null;
  }

  return dialogsConfigConstructor(dialogConfig);
};

export const listDialogConstructor = <T>({
  item,
  action,
  width,
}: ListDialogConfig<{ items: T[] }>): DialogConfig => {
  const dialogConfig: DialogConfig = {
    width: width ?? ModalWidth.MEDIUM,
    disable: false,
    disclaimer: "",
    header: {
      title: i18n.global.t(`modals.${action}.title`, { ...item, n: item?.items.length }),
      close: true,
    },
    content: {
      component: ListModal,
    },
    data: {
      item,
      action,
    },
    scrollable: true,
    footer: null,
  };
  return dialogsConfigConstructor(dialogConfig);
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const socAddonsEnabled = () => {
  return [
    SubscriptionAddon.SOC_EMAIL,
    SubscriptionAddon.SOC_CLOUD,
    SubscriptionAddon.SOC_ENDPOINT,
    SubscriptionAddon.SOC_ENDPOINT_DATA_GOVERNANCE,
    SubscriptionAddon.SOC_USER_DATA_GOVERNANCE,
    SubscriptionAddon.MDR,
  ].some((module) => !isAddonDisabled(module));
};

/**
 * Returns default disclaimer text for dialog component.
 * @param disable
 * @param disclaimer
 * @returns {string}
 */
export function getDisclaimerText(disable: boolean = false, disclaimer: string = ""): string {
  if (disable || isWorkspaceFrozen()) {
    return getDefaultDisclaimerText();
  }

  return disclaimer;
}

export function getDefaultDisclaimerText() {
  const mspRoute = isMspPortalRoute();
  if (isWorkspaceFrozen() && !mspRoute) {
    return i18n.global.t("general.subscriptionExpired");
  }

  return i18n.global.t("general.noPermissions");
}

/**
 * @deprecated
 *
 *
 * This function is deprecated. Instead, use {@link useAppsManagementModule}`
 * (from `apps-mgmt.module.ts`) and check its `context` property to determine
 * the current context.
 */
export function isMspPortalRoute() {
  const route = router.currentRoute.value;
  const rootRoute = route.matched[0]?.name;
  return rootRoute === RouteName.MSP;
}

/**
 * Transforms selection items from objects to array of strings.
 * @param selection
 * @param key
 * @returns {{include: string[], isAllPages: boolean, exclude: string[]}}
 */
export const getSelectionAsStringsArray = <T extends object>(
  selection: ISelection<T>,
  key: keyof { [P in keyof T as T[P] extends string ? P : never]: T[P] }
): ISelection<string> => {
  return {
    isAllPages: selection.isAllPages,
    include: selection.include.map((item) => item[key] as string),
    exclude: selection.exclude.map((item) => item[key] as string),
  };
};

/**
 * Returns if event/ticket belongs to the Device type. Maintains the same logic as BE has.
 * @returns {boolean}
 * @param eventType
 */
export const isDeviceTicket = (eventType: TicketType): boolean => {
  return [
    TicketType.MALWARE_IN_DEVICE,
    TicketType.ENDPOINT_VULNERABILITY,
    TicketType.WIFI_PHISHING,
    TicketType.WIFI_FORBIDDEN_NETWORK,
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE,
  ].includes(eventType);
};

/**
 * Checks if string contains only white spaces
 * @param str
 * @returns {boolean}
 */
export const onlySpaces = (str: string) => {
  return str?.length ? str.trim().length === 0 : true;
};

export function getPercentage(sum: number, target: number): number {
  return Math.round((target * 100) / sum);
}

export const downloadFile = (blob: Blob, fileName: string) => {
  if (!blob || !fileName) {
    console.warn("Can't download, blob or fileName is empty");
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
  link.remove();
};

export const downloadFileByLink = (downloadLink: string) => {
  const linkEl = document.createElement("a");
  linkEl.href = downloadLink;
  document.body.appendChild(linkEl);
  linkEl.click();
  document.body.removeChild(linkEl);
};

export const convertTimeFrameToUnix = ({ start, end }: TimeFrame): UnixTimeFrame => {
  return {
    fromTime: moment(start).startOf("day").utc().valueOf(),
    toTime: moment(end).endOf("day").utc().valueOf(),
  };
};

export const convertTimeFrameToUTCUnix = ({ start, end }: TimeFrame): UnixTimeFrame => {
  return {
    fromTime: moment.utc(start).startOf("day").valueOf(),
    toTime: moment.utc(end).endOf("day").valueOf(),
  };
};

/**
 * Get the number of days until a given timestamp.
 * If the date is the same day, it returns the days as is.
 * If the date is in the past, it prepends a '-' sign.
 *
 * @param {number} timestamp - The target timestamp in milliseconds.
 * @returns {string} - The formatted number of days until the timestamp.
 */
export const getDaysUntil = (timestamp: number): string => {
  const now = moment();
  const targetDate = moment(timestamp);

  // Handle same day, past, and future cases
  if (now.isSame(targetDate, "day")) {
    return i18n.global.t("general.days", { n: 0 }, 0);
  }

  return targetDate.isBefore(now) ? `-${targetDate.from(now, true)}` : targetDate.from(now, true);
};

/**
 * Creates two-way binding model with the same structure as BE response. Uses callback to update value (HTTP request in store).
 * @param initialValue - An initial value of the model. Should be a Ref.
 * @param updateCallback - Callback which will be called when a resulting variable will be updated.
 * @param restCallbackParameters - Rest of the parameters that are needed for the callback.
 */
export function useTwoWayBinding<T extends object, K extends any[], V>(
  initialValue: Ref<T>,
  updateCallback: (value: T, ...args: K) => V,
  ...restCallbackParameters: K
): WritableComputedRef<T> {
  function createDeepProxy(obj: T): T {
    return new Proxy(obj, {
      get(target, prop, receiver) {
        const result = Reflect.get(target, prop, receiver);
        if (typeof result === "object" && result !== null) {
          return createDeepProxy(result as T);
        }
        return result;
      },
      set(target, prop, value, receiver) {
        const result = Reflect.set(target, prop, value, receiver);
        // Trigger the update callback with a deep-cloned updated value
        updateCallback(cloneDeep(initialValue.value), ...restCallbackParameters);
        return result;
      },
    });
  }

  return computed<T>({
    get() {
      return createDeepProxy(initialValue.value);
    },
    set(newValue: T) {
      updateCallback(newValue, ...restCallbackParameters);
    },
  });
}

/**
 * @deprecated
 * @param addonOrModule
 */
export const getBetaTag = (addonOrModule: string) => {
  const workspaceStore = useWorkspaceStore();
  return (workspaceStore.workspace.betaComponents ?? []).find(
    (comp) => comp.name === addonOrModule
  );
};

export const isWorkspaceFrozen = () => {
  const workspaceStore = useWorkspaceStore();

  return workspaceStore.workspace.subscriptionType === SubscriptionType.FREEZE;
};

export const isWorkspaceInitial = () => {
  const workspaceStore = useWorkspaceStore();

  return workspaceStore.workspace.subscriptionType === SubscriptionType.INITIAL;
};

// Make all properties non optional so that parameters
// to functions can be passed properly (`keyof` of selected scope) and autocomplete works
type RequiredWorkspaceRolePermissions = Required<WorkspaceRolePermissions>;

export function isWorkspaceFrozenOrActionRestricted<
  T extends RolePermissionsScope,
  K extends keyof RequiredWorkspaceRolePermissions[T],
  V extends RequiredWorkspaceRolePermissions[T][K] extends NestedWorkspacePermissions
    ? NonNullable<RequiredWorkspaceRolePermissions[T][K]["editAccessModePermission"]>
    : undefined,
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  return isWorkspaceFrozen() || isActionRestricted(scopeName, key, nestedPermissionKey);
}

export function isActionRestricted<
  T extends RolePermissionsScope,
  K extends keyof RequiredWorkspaceRolePermissions[T],
  V extends RequiredWorkspaceRolePermissions[T][K] extends NestedWorkspacePermissions
    ? NonNullable<RequiredWorkspaceRolePermissions[T][K]["editAccessModePermission"]>
    : undefined,
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  const permissions = getPermissionsByScopeNameAndKey(scopeName, key);
  // Filter case when permissions doesn't exist
  if (!permissions) return true;

  // Filter case when permissions is just string
  if (typeof permissions === "string") {
    return permissions !== RolePermissionAccessMode.EDIT;
  }

  // Filter case when permissions is an object of such shape
  // {
  //   accessMode: RolePermissionAccessMode;
  //   editAccessModePermission?: {[key: string]: boolean}
  // };

  if (typeof permissions === "object") {
    // If no key provided, then check if it's edit or not
    if (!nestedPermissionKey) {
      return permissions.accessMode !== RolePermissionAccessMode.EDIT;
    }

    // If key is provided, then check the particular action
    if (
      nestedPermissionKey &&
      permissions.accessMode === RolePermissionAccessMode.EDIT &&
      permissions.editAccessModePermission
    ) {
      return permissions.editAccessModePermission
        ? // @ts-ignore
          !permissions.editAccessModePermission[nestedPermissionKey]
        : true;
    }
  }

  return true;
}

export function isAccessRestricted<
  T extends RolePermissionsScope = RolePermissionsScope,
  K extends
    keyof RequiredWorkspaceRolePermissions[T] = keyof WorkspaceRolePermissions[RolePermissionsScope],
>(scopeName: T, key: K) {
  const permissions = getPermissionsByScopeNameAndKey(scopeName, key);
  if (!permissions) return true;

  if (typeof permissions === "string") {
    return permissions === RolePermissionAccessMode.NO_ACCESS;
  }
  return permissions.accessMode === RolePermissionAccessMode.NO_ACCESS;
}

export function getPermissionsByScopeNameAndKey<
  T extends RolePermissionsScope = RolePermissionsScope,
  K extends
    keyof RequiredWorkspaceRolePermissions[T] = keyof WorkspaceRolePermissions[RolePermissionsScope],
>(scopeName: T, key: K) {
  const permissionsStore = usePermissionsStore();
  const currentWorkspacePermissions = toRaw(permissionsStore.currentWorkspacePermissions);
  return get(currentWorkspacePermissions, [scopeName, key]) as
    | NestedWorkspacePermissions
    | RolePermissionAccessMode
    | undefined;
}

export const hasSeparator = (str: string, separator = ",") => !!str?.includes(separator);

export const clearFileTypes = (fileTypes: string[]) => {
  return fileTypes.map((type) => {
    return type.replace(/(^[^A-Z0-9]+)|([^A-Z0-9]+$)/gi, "");
  });
};

/**
 * WARNING: I'm using fetch here because I want to avoid axios interceptor to intercept this requests.
 * These requests are public and does not require adding headers.
 */

const fixedEncodeURIComponent = (str: string) =>
  encodeURIComponent(str).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16)}`);

// helper for creating query string
const buildQuery = (params: object) => {
  if (!isObject(params)) return "";

  let query: string = Object.keys(params as object)
    .map((key) => {
      const paramValue = params[key as keyof typeof params];

      if (typeof paramValue === "object") {
        return buildQuery(paramValue);
      }

      return `${key}=${fixedEncodeURIComponent(paramValue)}`;
    })
    .filter((p) => !!p)
    .join("&");

  if (query) {
    query = `?${query}`;
  }

  return query;
};

export const getDownloadLink = async ({
  id,
  os,
  version,
}: {
  id: string;
  os?: string;
  version?: string;
}) => {
  const awsLambdaUrl =
    "https://a9yicb7x92.execute-api.us-east-1.amazonaws.com/bugatti-prod-get-url";
  const queryString = buildQuery({
    uuid: id,
    os,
    version,
    env: import.meta.env.VITE_CLIENT_ENV,
  });
  const response = await fetch(`${awsLambdaUrl}/${queryString}`);
  return await response.json();
};

export const getConvertedOSTypeForAWS = (
  osType: OsType.WINDOWS | OsType.OSX | OsType.LINUX
): "win" | "macos" | "linux" => {
  const osTypesMap = {
    [OsType.WINDOWS]: "win",
    [OsType.OSX]: "macos",
    [OsType.LINUX]: "linux",
  } as const;
  return osTypesMap[osType];
};
/**
 * Checks if semver version is greater that target semver version.
 * @param {string} targetVersion
 * @param {string} currentVersion
 * @returns {boolean}
 * @example
 * isNewerOrSameVersion('1.0', '2.0') // true
 * isNewerOrSameVersion('1.10', '1.10') // true
 * isNewerOrSameVersion('1.10', '1.11') // true
 * isNewerOrSameVersion('1.0', '1.0.1') // true
 * isNewerOrSameVersion('1.0.1', '1.0.10')// true
 * isNewerOrSameVersion('1.0.1', '1.0.1')// false
 * isNewerOrSameVersion('2.0', '1.0') // false
 * isNewerOrSameVersion('2', '1.0')// false
 * isNewerOrSameVersion('2.0.0.0.0.1', '2.1') // true
 * isNewerOrSameVersion('2.0.0.0.0.1', '2.0')// false
 * isNewerOrSameVersion('1.10.1.1', '1.10.1.2') // true
 * isNewerOrSameVersion('1.10', undefined) // false
 */
export function isNewerOrSameVersion(targetVersion: string, currentVersion: string): boolean {
  if (targetVersion === currentVersion) return true;
  const targetVersionParts = targetVersion.split(".");
  const currentVersionParts = currentVersion?.split(".") ?? [];
  const maxLength = Math.max(targetVersionParts.length, currentVersionParts.length);
  for (let i = 0; i < maxLength; i++) {
    const current = parseInt(currentVersionParts[i] || "0"); // parse int

    const target = parseInt(targetVersionParts[i] || "0"); // parse int
    if (current < target) return false;
    if (current > target) return true;
  }
  return true;
}

export function addWorkspaceHeaderIfNeeded(request: AxiosRequestConfig, workspaceId?: string) {
  if (workspaceId) {
    request.headers = {
      Workspace: workspaceId,
    };
  }
}

/**
 * Formats a given date or the current date/time using Moment.js.
 *
 * @param {MomentInput} [date] - The date to format. If omitted, the current date/time is used.
 * @param {string} [format="llll"] - The desired format for the date. Defaults to `"llll"`.
 *
 * ### Recommended Formats
 * While any valid Moment.js format string can be used, the following are recommended for consistency:
 * - `'LT'` → `4:29 PM`
 * - `'LTS'` → `4:29:59 PM`
 * - `'L'` → `11/15/2024`
 * - `'l'` → `11/15/2024`
 * - `'LL'` → `November 15, 2024`
 * - `'ll'` → `Nov 15, 2024`
 * - `'LLL'` → `November 15, 2024 4:29 PM`
 * - `'lll'` → `Nov 15, 2024 4:29 PM`
 * - `'LLLL'` → `Friday, November 15, 2024 4:29 PM`
 * - `'llll'` → `Fri, Nov 15, 2024 4:29 PM` (default)
 *
 * ### Examples
 * ```typescript
 * getFormattedDateTime(new Date(), "LL"); // Outputs: "November 15, 2024"
 * getFormattedDateTime();                // Outputs the current date/time in "Fri, Nov 15, 2024 4:29 PM" format
 * getFormattedDateTime("2024-11-15", "L"); // Outputs: "11/15/2024"
 * ```
 *
 * @returns {string | undefined} The formatted date/time as a string, or `undefined` if no date is provided.
 */
export const getFormattedDateTime = (date?: MomentInput, format: string = "llll") => {
  if (date) {
    return moment(date).format(format);
  }
};

/**
 * Formats a given date relative to the current date/time using Moment.js.
 *
 * @param {MomentInput} [date] - The date to format. If omitted, the function returns `undefined`.
 *
 * ### Description
 * This function uses Moment.js's `.calendar()` method to format a given date in a relative, human-readable way.
 * It outputs strings such as "Today at 4:30 PM", "Yesterday at 3:00 PM", or "Last Monday at 2:15 PM" depending
 * on the proximity of the date to the current time.
 *
 * ### Examples
 * ```typescript
 * formatDistanceToNow(new Date());
 * // Outputs: "Today at 4:29 PM" (assuming the current time is 4:29 PM)
 *
 * formatDistanceToNow("2024-11-14");
 * // Outputs: "Thursday at 12:00 AM" (or a similar string based on your locale)
 *
 * formatDistanceToNow(moment().subtract(1, "days").toDate());
 * // Outputs: "Yesterday at 4:29 PM"
 *
 * formatDistanceToNow();
 * // Outputs: undefined (no date provided)
 * ```
 *
 * @returns {string | undefined} A human-readable string describing the relative time, or `undefined` if no date is provided.
 */
export const formatDistanceToNow = (date?: MomentInput) => {
  if (date) {
    return moment(date).calendar();
  }
};

// TODO: investigate https://medium.com/@rado.sabo/abortcontroller-abort-ongoing-calls-in-vue-with-axios-interceptor-584c9f0566a6
// if it's suitable for our case
export function createAbortController(controller?: AbortController): AbortController {
  if (controller) {
    controller.abort();
  }
  controller = new AbortController();
  return controller;
}

export const isCloudAppConnected = (serviceName: Service) => {
  if (!useCloudAppsStore().connectedServices?.length) return;

  return useCloudAppsStore().connectedServices.some((service) => service.name === serviceName);
};

export const isSecurityAwarenessAllowedFor = (serviceName: Service) => {
  if (!useCloudAppsStore().connectedServices?.length) return;

  return useCloudAppsStore().connectedServices.some(
    (service) => service.name === serviceName && service.securityAwarenessAllowed
  );
};

export const getWorkspaceFromRedirect = async (
  workspaceId: string
): Promise<WorkspaceDetails | null> => {
  if (!workspaceId) {
    return null;
  }

  const { baseURL } = api.getCurrentWorkspace;
  const resultUrl = `${baseURL}workspaces/current`;

  try {
    // Fetch is used to avoid endless loop in the interceptor since backend returns 401 if workspace doesn't exist
    const data = await fetch(resultUrl, {
      headers: {
        Authorization: `Bearer ${useAccountStore().token}`,
        Workspace: workspaceId,
      },
    });

    if (!data.ok) {
      return null;
    }

    return data.json();
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const setWorkspace = async (workspace: WorkspaceDetails): Promise<void> => {
  const workspaceStore = useWorkspaceStore();
  workspaceStore.setWorkspace(workspace);
  useAppsManagementModule().handleAppsManagement(workspace.workspaceId, false);
  await workspaceStore.getCurrentWorkspace();
};

export function hasSocAddonInModule(module: SubscriptionModuleInfo) {
  return Boolean(
    module.addons.find((addon: SubscriptionAddonInfo) => {
      return (addon.name.includes("soc") || addon.name === SubscriptionAddon.MDR) && addon.enabled;
    })
  );
}

export function humanizeFileSize(size: number | undefined) {
  if (!size) return "0 B";
  const i = Math.floor(Math.log(size) / Math.log(1000));
  return +(size / Math.pow(1000, i)).toFixed(2) + " " + ["B", "kB", "MB", "GB", "TB"][i];
}

export function getCountryOrStateName(key: string): string {
  if (usaStatesList[key]) {
    return `US - ${usaStatesList[key]}`;
  }

  if (countriesList[key]) {
    return countriesList[key];
  }

  return "";
}

/**
 * Extracts domain from the email address (everything after '@' symbol). Returns undefined if domain is not found.
 * @param {string} email  Email address
 * @returns {string|undefined}
 */
export const getDomainFromEmail = (email: string | undefined): string | undefined => {
  const [, domain] = email?.split("@") ?? [];
  return domain || undefined;
};

/**
 * Retrieves the current operating system version based on the user's browser navigator information.
 * @returns {string} The OS version, either 'win', 'macos', or null if the OS is unrecognized.
 */
export const getOSVersion = (): "win" | "macos" | null => {
  const appVersion = navigator?.appVersion ?? "";
  let osName = null;
  if (appVersion.indexOf("Win") !== -1) {
    osName = "win" as const;
  }
  if (appVersion.indexOf("Mac") !== -1) {
    osName = "macos" as const;
  }
  return osName;
};

/**
 * Makes request to workspaces and identifies if workspace is available for particular customer.
 * @param workspaceId
 * @returns {Promise<boolean>}
 */
export const checkIfWorkspaceAvailableForCustomer = async (workspaceId: string) => {
  if (!workspaceId) {
    return false;
  }

  const userPermissionsRequest = {
    ...api.getUserPermissions(workspaceId),
  };

  try {
    const { data } = await axiosInstance.request<UserPermissionsResponse>(userPermissionsRequest);
    return !isEmpty(data?.localRole);
  } catch (e) {
    console.error(e);
    return false;
  }
};

/**
 * Returns days remaining in free trial
 * @param trialEnds {number} timestamp
 * @returns {string} "x days" or undefined
 */
export const getDaysUntilTrialEnds = (trialEnds?: number | null) => {
  const currentTime = moment().utc();
  const trialEndsTime = moment(trialEnds).utc();
  // 'moment().duration().humanize' receives thresholds as param -
  // it means that everything more a second should be shown as an hour and until 365 days we show days, not months (180 days, not 6 months) as per Carmel's request
  // The case more than 365 days shouldn't worry us as it's for Trial and maximum trial should be only 2 months (60 days)
  return moment.duration(trialEndsTime.diff(currentTime)).humanize({ d: 365, m: 1, s: 1, ss: 1 });
};

/**
 * Converts a given size in bytes to a human-readable format.
 *
 * @param {number} bytes - The size in bytes to be converted.
 * @param {number} [decimals=2] - The number of decimal places to include in the output. Defaults to 2 if not provided.
 * @param {boolean} [kib=false] - If true, uses binary (kibibyte) units; if false, uses decimal (kilobyte) units. Defaults to false.
 * @returns {string} The formatted size as a string, e.g., "12.34 MB" or "0 Bytes" if the input is 0.
 *
 * @example
 * // Convert 1500 bytes to human-readable format in decimal
 * console.log(convertSize(1500)); // "1.5 KB"
 *
 * @example
 * // Convert 1500 bytes to human-readable format in binary
 * console.log(convertSize(1500, 2, true)); // "1.46 KiB"
 *
 * @example
 * // Convert 0 bytes
 * console.log(convertSize(0)); // "0 Bytes"
 *
 * @example
 * // Handle non-numeric input
 * console.log(convertSize('abc')); // "Not a number"
 */
export const convertSize = (bytes: number, decimals: number = 2, kib: boolean = false) => {
  kib = kib || false;
  if (bytes === 0) return "0 Bytes";
  if (isNaN(parseFloat(bytes.toString())) && !isFinite(bytes)) return "Not a number";
  const k = kib ? 1024 : 1000;
  const dm = decimals != null && !isNaN(decimals) && decimals >= 0 ? decimals : 2;
  const sizes = kib
    ? ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB", "BiB"]
    : ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB", "BB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Remove malicious links from email content so that Google malware scanner doesn't analyze them and mark site as dangerous
 * @param {string} emailContent
 */
export const removeMaliciousLinksFromContent = (emailContent: string) => {
  const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

  // Replace <wbr> tags
  emailContent = emailContent.replace(/<wbr>/g, "");

  // Replace URLs
  return emailContent.replace(urlRegex, "");
};

export const getSelectedModules = (subscription: Subscription): SubscriptionModule[] => {
  return Object.keys(subscription.modules)
    .map((val) => {
      const name = val as SubscriptionModule;
      return {
        name,
        enabled: subscription.modules[name].enabled,
      };
    })
    .filter((v) => v.enabled)
    .map((v) => v.name);
};

export const isModuleIncludedInBundle = (module: string, subscription: Subscription) =>
  intersection(bundlesForModules[module as SubscriptionModule], subscription.bundles).length > 0;

export const isAddonIncludedInBundle = (addon: string, subscription: Subscription) =>
  intersection(
    bundlesForAddons[addon as Exclude<SubscriptionAddon, "soc" | "outboundGateway">],
    subscription.bundles
  ).length > 0;

export const getSelectedAddons = (subscription: Subscription): SubscriptionAddon[] => {
  const selectedAddons: SubscriptionAddon[] = [];
  Object.keys(subscription.modules).forEach((val) => {
    subscription.modules[val as SubscriptionModule].addons.forEach((addon) => {
      if (addon.enabled) {
        selectedAddons.push(addon.name);
      }
    });
  });
  return selectedAddons;
};

/**
 * Determines the browser's language and maps it to the `WorkspaceLocale` enum.
 * The function first attempts to find an exact match in `WorkspaceLocale`.
 * If no exact match exists, it tries to find a locale that matches the language prefix.
 * When neither is found, it defaults to `WorkspaceLocale.EN_US`.
 *
 * @returns {WorkspaceLocale} The browser's language mapped to `WorkspaceLocale`, or `WorkspaceLocale.EN_US` if no match is found.
 *
 * @example
 * // Assume the browser's language is "es-MX" (partial match for Spanish)
 * const locale = getBrowserLocale();
 * console.log(locale); // Output: WorkspaceLocale.ES
 *
 * @example
 * // Assume the browser's language is "it-IT" (partial match for Italian)
 * const locale = getBrowserLocale();
 * console.log(locale); // Output: WorkspaceLocale.IT
 *
 * @example
 * // Assume the browser's language is "fr" (not in WorkspaceLocale)
 * const locale = getBrowserLocale();
 * console.log(locale); // Output: WorkspaceLocale.EN_US
 *
 * @example
 * // Assume the browser's languages are ["es", "it-IT"] (navigator.languages array)
 * const locale = getBrowserLocale();
 * console.log(locale); // Output: WorkspaceLocale.ES
 *
 * @example
 * // Assume the browser's language is "fr-CA" (exact match precedence over prefix)
 * const locale = getBrowserLocale();
 * console.log(locale); // Output: WorkspaceLocale.FR_CA
 */
export const getBrowserLocale = (): WorkspaceLocale => {
  const browserLanguage = navigator.language || (navigator.languages ?? [])[0];
  if (!browserLanguage) return WorkspaceLocale.EN_US;
  const locales: WorkspaceLocale[] = Object.values(WorkspaceLocale);

  // Prefer exact locale matches before falling back to prefix matching.
  const normalizedBrowserLanguage = browserLanguage.toLowerCase();

  const exactMatch = locales.find((locale) => locale.toLowerCase() === normalizedBrowserLanguage);
  if (exactMatch) return exactMatch;

  const partialMatch = locales.find((locale) =>
    normalizedBrowserLanguage.startsWith(locale.toLowerCase())
  );

  return partialMatch || WorkspaceLocale.EN_US;
};

export const resetPersistedStores = () => {
  const subdomain = extractCoroSubdomain(window.location.hostname);
  useMyAccountStore().$reset();
  useFiltersStore().$reset();
  useMspStore().$reset();
  useAppsManagementModule().$reset();
  useAiAssistantStore().$reset();
  subdomain ? useWorkspaceStore().resetStateWithoutBranding() : useWorkspaceStore().$reset();
  usePermissionsStore().$reset();
};

/**
 * Merges the default subscription modules configuration with the provided module settings.
 *
 * This function ensures that enabled states are converted to booleans and adds unique addons
 * from both the new and default configurations, sorted by name.
 *
 * Note: lodash's defaultsDeep function behaves such that if a value exists in both
 * arrays, it will add the value again instead of maintaining uniqueness.
 *
 * @returns {ModulesSettings} Merged modules settings with defaults applied.
 */
export const setDefaultModules = (newModules: ModulesSettings): ModulesSettings => {
  const defaultModules = cloneDeep(defaultSubscriptionModulesConfig);
  const modules: ModulesSettings = {} as ModulesSettings;

  Object.keys(defaultModules).forEach((key) => {
    const module = key as SubscriptionModule;
    const currentAddons: SubscriptionAddonInfo[] = get(newModules[module], "addons", []);
    const defaultAddons: SubscriptionAddonInfo[] = defaultModules[module].addons;

    // Use unionBy to merge the addons uniquely by their name
    const mergedAddons = unionBy(currentAddons, defaultAddons, "name");
    const sortedAddons = sortBy(mergedAddons, ["name"]);

    modules[module] = {
      enabled: Boolean(newModules[module]?.enabled),
      addons: sortedAddons,
    };
  });

  return modules;
};

export const navigateWithReload = (routeName: RouteName) => {
  const resolvedRoute = router.resolve({ name: routeName });
  window.location.href = resolvedRoute.href;
};

/**
 * Navigates to a route while updating workspace and permissions
 * Used for MSP workspace navigation to switch workspace context
 */
export const navigateToRoute = async (
  workspaceId: string,
  routeName: string,
  query: LocationQueryRaw = {}
) => {
  const baseWorkspaceStore = useWorkspaceStore();
  await baseWorkspaceStore.updateWorkspaceAndPermissions(workspaceId, false);
  useResetStore().allExceptIgnoreList();
  await useAccountStore().checkMspAccess();
  await router.push({ name: routeName, query });
};

/**
 * Provides a set of utility methods to reset all active Pinia stores or specific stores individually.
 * The function dynamically generates reset methods for each store within the active Pinia instance
 * and includes a method to reset all stores at once.
 *
 * @throws {Error} If there is no active Pinia instance available.
 *
 * @returns {UseResetStore} An object containing:
 * - Individual reset methods for each store (keyed by the store name).
 * - An `all` method to reset all stores at once.
 * - An `allExceptIgnoreList` method to reset all stores except some which are in ignore list
 * @example
 * // Resetting all stores
 * const resetStore = useResetStore();
 * resetStore.all();
 *
 * @example
 * // Resetting a specific store
 * const resetStore = useResetStore();
 * resetStore['tasksStore'](); // Resets the 'tasksStore'
 *
 * @example
 * // Dynamically check for a store and reset it
 * const resetStore = useResetStore();
 * if (resetStore['settingsStore']) {
 *   resetStore['settingsStore']();
 * }
 * @example
 * // Resetting all stores except those that are in default ignore list
 * const resetStore = useResetStore();
 * resetStore.allExceptIgnoreList();
 *
 * @example
 * // Resetting all stores except those that are in ignore list that you can provide
 * const resetStore = useResetStore();
 * resetStore.allExceptIgnoreList(["account"]);
 */
export const useResetStore = (): UseResetStore => {
  const pinia = getActivePinia() as ExtendedPinia;
  const defaultIgnoreList: StoreModule[] = [
    StoreModule.ACCOUNT,
    StoreModule.WEBSOCKET,
    StoreModule.MY_ACCOUNT,
    StoreModule.REDIRECTS,
    StoreModule.APPS_MGMT,
    StoreModule.WORKSPACE,
    StoreModule.PERMISSIONS,
  ];
  if (!pinia) {
    throw new Error("There is no active Pinia instance");
  }

  const resetStore: UseResetStore = {} as UseResetStore;

  pinia._s.forEach((store, name) => {
    resetStore[name] = () => store.$reset();
  });

  resetStore.all = () => pinia._s.forEach((store) => store.$reset());

  resetStore.allExceptIgnoreList = (ignoreList: StoreModule[] = defaultIgnoreList) =>
    pinia._s.forEach((store) => {
      if (!ignoreList.includes(store.$id as StoreModule)) {
        store.$reset();
      }
    });

  return resetStore;
};

/**
 * Sorts an object by its values in ascending or descending order.
 *
 * @param {Record<string, number>} obj - The object to sort.
 * @param {"asc" | "desc"} [sortDirection="desc"] - Sorting order (ascending or descending).
 * @returns {Record<string, number>} A new object sorted by values.
 *
 * @example
 * const scores = { alice: 10, bob: 5, charlie: 8 };
 * console.log(sortByValue(scores, "asc"));
 * // Output: { bob: 5, charlie: 8, alice: 10 }
 *
 * @example
 * const ratings = { apple: 4.8, banana: 3.2, cherry: 4.5 };
 * console.log(sortByValue(ratings, "desc"));
 * // Output: { apple: 4.8, cherry: 4.5, banana: 3.2 }
 */
export const sortObjectByValue = (
  obj: Record<string, number>,
  sortDirection: "asc" | "desc" = "desc"
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(obj).sort(([, v1], [, v2]) => (sortDirection === "desc" ? v2 - v1 : v1 - v2))
  );

export const getOpenSearchQuery = (
  newPagination: Pagination,
  oldPagination: Pagination,
  pages: number,
  cursor: OpenSearchCursor
) => {
  let openSearchQuery: OpenSearchQuery = firstPageQuery;
  if (pages > 1) {
    if (newPagination.page === pages - 1) {
      openSearchQuery = lastPageQuery;
    } else if (newPagination.page === 0) {
      openSearchQuery = firstPageQuery;
    } else if (newPagination.page > oldPagination.page) {
      openSearchQuery = cursor.nextPage!;
    } else if (newPagination.page < oldPagination.page) {
      openSearchQuery = cursor.prevPage!;
    }
  }
  return openSearchQuery;
};

export const parseOpenSearchQuery = (obj: OpenSearchQuery) => {
  const result: OpenSearchQuery = cloneDeep({ ...obj });

  for (const key in result) {
    if (Array.isArray(result[key])) {
      result[key] = result[key].join(",");
    }
  }

  return result;
};

export const isSocAddon = (addon: SubscriptionAddon) => {
  const socAddons = [
    SubscriptionAddon.SOC_ENDPOINT,
    SubscriptionAddon.SOC_EMAIL,
    SubscriptionAddon.SOC_NETWORK,
    SubscriptionAddon.SOC_ENDPOINT_DATA_GOVERNANCE,
    SubscriptionAddon.MDR,
    SubscriptionAddon.SOC_CLOUD,
    SubscriptionAddon.SOC_USER_DATA_GOVERNANCE,
  ];
  return socAddons.includes(addon);
};

export const isWorkspaceNewAndInDemoMode = () => {
  const workspaceStore = useWorkspaceStore();
  const { demoModeEndDate, demoModeEnabled, subscriptionType } = workspaceStore.workspace;
  return Boolean(
    subscriptionType === SubscriptionType.INITIAL && demoModeEnabled && demoModeEndDate
  );
};

export const openMovingToTrialModal = (action: DemoWillStartModal, successCallback: () => void) => {
  const dialogsStore = useDialogsStore();
  dialogsStore.openDialog({
    ...confirmationDialogsConfigConstructor({
      action,
      disable: false,
      item: {},
      callback: async () => {
        await nextTick(() => {
          successCallback();
        });
      },
    }),
    header: {
      title: i18n.global.t(`modals.${action}.title`),
      titleClass: "text-red-dark",
      titleIcon: "warning",
    },
  });
};

/**
 * Masks the local part of an email address for privacy, preserving the first and last characters.
 * If the local part is 2 characters or fewer, only a single `*` is appended after it.
 *
 * @param email - The full email address to mask.
 * @returns The masked email address.
 *
 * @example
 * maskEmail("john.doe@example.com") // "j*******e@example.com"
 *
 * @example
 * maskEmail("ab@example.com") // "ab*@example.com"
 *
 * @example
 * maskEmail("a@example.com") // "a*@example.com"
 */
export const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split("@");

  if (localPart.length <= 2) {
    return `${localPart}*@${domain}`;
  }

  const maskedPart = "*".repeat(localPart.length - 2);
  return `${localPart[0]}${maskedPart}${localPart[localPart.length - 1]}@${domain}`;
};

export const trackGtmEvent = (event: string, payload: string) => {
  const workspaceStore = useWorkspaceStore();
  const account = useAccountStore();
  const gtm = useGtm();
  if (gtm?.enabled()) {
    gtm.trackEvent({
      event,
      payload,
      subscriptionType: workspaceStore?.workspace?.subscriptionType || "",
      workspaceType: workspaceStore?.workspace?.type || "",
      workspaceId: workspaceStore?.workspace?.workspaceId || "",
      userDomain: getDomainFromEmail(account.email) || "",
    });
  }
};

function handleCloudAppsInternalRedirects(routeQuery: LocationQuery): void {
  if (routeQuery.grantPermissions === "true" && routeQuery.service) {
    openConnectCloudAppsModal(
      ServiceAction.CONNECTION_STATUS,
      useCloudAppsStore().services.find(
        (service) => service.name === (routeQuery.service as Service)
      )!
    );

    return;
  }
}

export function processCloudAppsQueryParams(routeQuery: LocationQuery): void {
  if (routeQuery.internal === "true") {
    handleCloudAppsInternalRedirects(routeQuery);

    return;
  }

  if (routeQuery.service === Service.OFFICE_365 && routeQuery.grantPermissions === "true") {
    openConnectCloudAppsModal(ServiceAction.CONNECT, {
      name: routeQuery.service as Service,
      serviceStatus: ServiceStatus.CONNECTED,
    });

    return;
  }

  if (routeQuery.isNextStep === "true" && routeQuery.grantPermissions === "true") {
    openConnectCloudAppsModal(ServiceAction.GRANT_PRIVILEGES, {
      name: routeQuery.service as Service,
    });

    return;
  }

  if (routeQuery.isNextStep === "true") {
    openConnectCloudAppsModal(ServiceAction.CONNECT, { name: routeQuery.service as Service });

    return;
  }

  if (routeQuery.noServicesConnected === "true") {
    openConnectCloudAppsModal(ServiceAction.CONNECT, {
      showOnlyPrimaryCloudApps: routeQuery.showOnlyPrimaryCloudApps === "true",
    });

    router.replace({ query: {} });

    return;
  }

  if ([Service.DROPBOX, Service.SLACK].includes(routeQuery.service as Service)) {
    openConnectCloudAppsModal(ServiceAction.CONNECT, {
      name: routeQuery.service as Service,
      serviceStatus: ServiceStatus.CONNECTED,
    });
  }
}

/**
 * Recursively creates a deep clone of the input object or array,
 * removing all fields that match the specified key name.
 *
 * @template T - The type of the input and resulting object.
 * @param input - The object or array to process.
 * @param keyToRemove - The key to remove from all nested levels (e.g., 'temporaryId').
 * @returns A new deep-cloned version of the input with all matching keys removed.
 */
export function removeKeyDeepClone<T extends object>(input: T, keyToRemove: string): T {
  if (!isObject(input)) return input;

  if (isArray(input)) {
    return input.map((item) => removeKeyDeepClone(item, keyToRemove)) as unknown as T;
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(input)) {
    if (key === keyToRemove) continue;
    result[key] = removeKeyDeepClone(value, keyToRemove);
  }

  return result as T;
}

export const canOverrideGlobalPolicy = (currentSection: GlobalPolicySection) => {
  const { workspace } = useWorkspaceStore();
  // if no global settings info then everything is editable
  if (isEmpty(workspace.mspPolicyShortInfo)) return true;
  const isSectionPresentInGlobalPolicy =
    workspace.mspPolicyShortInfo.sections.includes(currentSection);
  return workspace.allowPolicyOverriding || !isSectionPresentInGlobalPolicy;
};

export const showHiddenBanner = (banner: CoroBanner) => {
  const hiddenBannersList = useWorkspaceStore().hiddenBanners || [];
  return !hiddenBannersList.includes(banner);
};

export const flattenTicketDetails = (ticketDetails: TicketDetails) => {
  const sections = ticketDetails?.sections;
  const attachmentName =
    sections?.emailMetaData?.attachments?.[0]?.fileName ||
    sections?.emailMetaData?.attachment?.fileName;
  const deviceUser = getDeviceUser();

  function getDeviceUser() {
    const lastLoggedInUsers = ticketDetails?.lastLoggedInUsers ?? [];
    return lastLoggedInUsers.length ? lastLoggedInUsers[0] : ticketDetails?.sections?.triggeredUser;
  }

  const subTriggerKeys = ticketDetails?.sections?.aggregatedEventTriggers ?? [];
  const recipientsList = sections?.emailMetaData?.recipients ?? [];
  const recipients =
    recipientsList.length > 1
      ? `${recipientsList[0]} +${recipientsList.length - 1}`
      : recipientsList[0] || "";

  return {
    ...ticketDetails,
    ...sections?.malwareMetaData,
    ...sections?.emailMetaData,
    ...sections?.deviceMetaData,
    ...sections?.networkMetaData,
    ...sections?.malwareMetaData,
    ...sections?.infectedProccessMetaData,
    ...sections?.systemMetaData?.proxyMisconfiguration,
    ...sections?.edrMetaData,
    attachmentName,
    recipients,
    unencryptedDriveName: sections?.deviceMetaData?.unencryptedFileStorage?.name,
    deviceUser: deviceUser,
    triggeredUser: sections.triggeredUser,
    serviceName: i18n.global.t(`services.${ticketDetails.sections.serviceName}`),
    endTime: getFormattedDateTime(ticketDetails.endTime, "lll"),
    startTime: getFormattedDateTime(ticketDetails.startTime, "lll"),
    eventHappened: sections?.counter,
    countryName: sections?.locations[0]?.countryName,
    ip: sections?.locations[0]?.ip,
    subTriggers: subTriggerKeys
      .map((i) => i18n.global.t(`tickets.eventSubTriggers.${i}`))
      .join(", "),
    dlpSharedBy: sections?.filesMetaData?.[0]?.sharedBy ?? "",
    sharedFileName: sections?.filesMetaData?.[0]?.fileName ?? "",
    dlpSharedWith: sections?.filesMetaData?.[0]?.sharedWith?.[0] ?? "",
    userWhoSharedDlp: sections?.userDataAccessViolation?.users?.[0] || "",
    driveName: sections?.deviceMetaData?.scannedFileStorage?.name || "",
    impossibleTravelerEvents: sections?.cloudThreatMetaData?.length || "",
  };
};

export const getVersionsList = <T extends { subheader?: true; name: string }>(
  selectedOsVersion: string | null | undefined,
  osVersionsFromStore: DeviceOsOrClientVersions,
  clientVersionsFromStore: DeviceOsOrClientVersions
): {
  osVersions: T[];
  clientVersions: T[];
} => {
  const allOsTypes = Object.keys(osVersionsFromStore) as (keyof DeviceOsOrClientVersions)[];

  const filteredOsTypes = selectedOsVersion
    ? allOsTypes.filter((osType) => {
        const osVersionsForType = osVersionsFromStore[osType] ?? [];
        return osVersionsForType.includes(selectedOsVersion);
      })
    : allOsTypes;

  const osVersions: T[] = [];
  const clientVersions: T[] = [];

  allOsTypes.forEach((osType) => {
    if (osVersionsFromStore[osType]?.length) {
      osVersions.push(
        {
          subheader: true,
          name: i18n.global.t(`general.osVersion.${osType}`),
        } as T,
        ...(osVersionsFromStore[osType] ?? []).map((v: string) => ({ name: v }) as T)
      );
    }
  });

  filteredOsTypes.forEach((osType) => {
    if (clientVersionsFromStore?.[osType]?.length) {
      clientVersions.push(
        {
          subheader: true,
          name: i18n.global.t(`general.osVersion.${osType}`),
        } as T,
        ...(clientVersionsFromStore[osType] ?? [])
          .slice()
          .sort()
          .reverse()
          .map((v: string) => ({ name: v }) as T)
      );
    }
  });

  return { osVersions, clientVersions };
};

/**
 * Gets the latest version for a specific operating system type from an array of available versions.
 * Versions are sorted by marketing version in descending order (newest first).
 *
 * @param {DeviceVersionItem[]} availableVersions - Array of device version items to search through
 * @param {OsType} osType - The operating system type to filter by (e.g., WINDOWS, OSX, LINUX)
 * @returns {DeviceVersionItem | null} The latest version item for the specified OS type, or null if no versions exist for that OS type
 * @example
 * const versions = [
 *   { id: "1", marketingVersion: "1.0.0", osType: OsType.WINDOWS, ... },
 *   { id: "2", marketingVersion: "2.0.0", osType: OsType.WINDOWS, ... }
 * ];
 * const latest = getLatestVersionByOsType(versions, OsType.WINDOWS);
 * // Returns: { id: "2", marketingVersion: "2.0.0", osType: OsType.WINDOWS, ... }
 */
export const getLatestVersionByOsType = (
  availableVersions: DeviceVersionItem[],
  osType: OsType
): DeviceVersionItem | null => {
  const versionsForOsType = availableVersions.filter((version) => version.osType === osType);

  if (versionsForOsType.length === 0) {
    return null;
  }

  const sortedVersions = versionsForOsType.sort((a, b) => {
    if (isNewerOrSameVersion(a.marketingVersion, b.marketingVersion)) {
      return 1;
    }
    if (isNewerOrSameVersion(b.marketingVersion, a.marketingVersion)) {
      return -1;
    }
    return 0;
  });

  return sortedVersions[0];
};

/**
 * Gets the latest version for each target operating system type (WINDOWS, OSX, LINUX).
 * Returns an array containing the latest version for each OS type that has available versions.
 *
 * @param {DeviceVersionItem[]} availableVersions - Array of device version items to search through
 * @returns {DeviceVersionItem[]} Array of the latest version items for each target OS type (WINDOWS, OSX, LINUX), excluding OS types with no available versions
 * @example
 * const versions = [
 *   { id: "1", marketingVersion: "2.0.0", osType: OsType.WINDOWS, ... },
 *   { id: "2", marketingVersion: "1.0.0", osType: OsType.WINDOWS, ... },
 *   { id: "3", marketingVersion: "3.0.0", osType: OsType.OSX, ... }
 * ];
 * const latestVersions = getLatestVersionsByOsType(versions);
 * // Returns: [
 * //   { id: "1", marketingVersion: "2.0.0", osType: OsType.WINDOWS, ... },
 * //   { id: "3", marketingVersion: "3.0.0", osType: OsType.OSX, ... }
 * // ]
 */
export const getLatestVersionsByOsType = (
  availableVersions: DeviceVersionItem[]
): DeviceVersionItem[] => {
  const targetOsTypes = [OsType.WINDOWS, OsType.OSX, OsType.LINUX];

  return targetOsTypes
    .map((osType) => getLatestVersionByOsType(availableVersions, osType))
    .filter((version) => version !== null) as DeviceVersionItem[];
};

export const createFindingTemplate = (finding: Finding, ticket: TicketDetails) => {
  const TICKETS_SUPPORTING_FINDINGS_SOURCE = [
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL,
    TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL,
  ];

  let template = "";
  const quote = escape(finding.quote);
  const longQuote = escape(finding.longQuote);
  const filePath = finding.filePath;
  const virusName = finding.virusName;

  if (TICKETS_SUPPORTING_FINDINGS_SOURCE.includes(ticket?.eventType!)) {
    if (finding.source === FindingDetectedInType.ATTACHMENT) {
      const attachmentsArray = ticket!.sections.emailMetaData.attachments;
      const attachment = attachmentsArray
        ? attachmentsArray.find(({ fileName }) => filePath.includes(fileName))
        : ticket!.sections.emailMetaData.attachment;

      if (attachment?.fileName && filePath.includes(attachment?.fileName)) {
        template += i18n.global.t("modals.dlpFindings.detectedInFile", {
          fileName: attachment!.fileName,
          fileSize: convertSize(attachment!.fileSize, 1),
        });
      }
    } else if (finding.source) {
      template += i18n.global.t("modals.dlpFindings.detectedInGeneral", {
        detectedIn: i18n.global.t(`modals.dlpFindings.findingType.${finding.source}`),
      });
    }
  }

  if (finding.typeUiView) {
    template += i18n.global.t("modals.dlpFindings.type", {
      type: finding.typeUiView,
    });
  } else {
    template += i18n.global.t("modals.dlpFindings.type", {
      type: i18n.global.t(`ticketDetails.incidentFindings.type.${finding.type}`),
    });
  }
  if (longQuote) {
    // if quote present, highlight it, otherwise show longQuote as it is
    if (quote && longQuote.includes(quote)) {
      const highlightedTextStartIndex = longQuote.indexOf(quote);
      const highlightedTextEndIndex = longQuote.indexOf(quote) + quote.length;
      const textBeforeHighlight = longQuote.substring(0, highlightedTextStartIndex);
      const textAfterHighlight = longQuote.substring(highlightedTextEndIndex, longQuote.length);
      const highlightedText = quote;
      template =
        template +
        i18n.global.t("modals.dlpFindings.longQuote", {
          textBeforeHighlight,
          textAfterHighlight,
          highlightedText,
        });
    } else {
      template =
        template +
        i18n.global.t("modals.dlpFindings.longQuoteWithoutHighlight", {
          longQuote,
        });
    }
  }
  if (!longQuote && quote) {
    if (finding.type === "BLOCKLISTED_SENDER") {
      // Split sender finding quote and list them one by one in a column
      template += i18n.global.t("modals.dlpFindings.quotePlain", {
        quote: quote
          .split(" ")
          .map((v) => `"${v}"`)
          .join("<br>"),
      });
    } else {
      template += i18n.global.t("modals.dlpFindings.quote", { quote });
    }
  }
  if (virusName) {
    template += i18n.global.t("modals.dlpFindings.virusName", { virusName });
  }
  if (!finding.source && filePath) {
    template += i18n.global.t("modals.dlpFindings.fileName", {
      fileName: filePath,
    });
  }

  return template;
};

/**
 * Optimizes dynamic components by preventing unnecessary remounts.
 *
 * When a component definition is stored in a reactive state (ref/reactive)
 * and used in `<component :is="...">`, Vue wraps it in a Proxy.
 * Parent component updates can cause Vue to see a new Proxy,
 * triggering a costly remount and losing the component's state.
 *
 * This function uses:
 * 1. `toRaw(comp)`: To get the original component from the Proxy.
 * 2. `markRaw(rawComp)`: To tell Vue to never make it reactive again.
 *
 * This preserves the component's state and improves performance.
 *
 * @param {Component} comp - The Vue component, potentially wrapped in a reactive Proxy.
 * @returns {Component} The raw, non-reactive component definition, safe for `:is`.
 *
 * @example
 * // const comp = computed(() => markComponentAsPlain(MyComponentA));
 * // <component :is="comp" />
 */
export const markComponentAsPlain = (comp: Component): Component => {
  // 1. Get the original object from inside the Proxy (if it is one).
  const rawComp = toRaw(comp);
  // 2. Mark the raw object so Vue never makes it reactive again.
  return markRaw(rawComp);
};

/**
 * Sorts an array of ticket actions according to specific business rules:
 * 1. 'Mark as Processed/Unprocessed'
 * 2. All other actions (sorted by severity, critical -> low) and alphabetically by name if they have the same severity
 * 3. 'Contact User'
 *
 * @returns {Array<TicketActionWithSeverity>} A new, sorted array.
 */
export const sortTicketActions = (
  actions: TicketActionWithSeverity[]
): TicketActionWithSeverity[] => {
  const severityOrder = {
    [TicketActionSeverity.CRITICAL]: 3,
    [TicketActionSeverity.MEDIUM]: 2,
    [TicketActionSeverity.LOW]: 1,
  };

  const getSeverity = (action: TicketActionWithSeverity) => severityOrder[action.severity] ?? 0;

  // Gets the main sorting group for an action.
  const getSortGroup = (action: TicketActionWithSeverity) => {
    const { name } = action;
    if (name === TicketAction.MARK_AS_PROCESSED || name === TicketAction.MARK_AS_UNPROCESSED) {
      return 1; // Always first
    }
    if (name === TicketAction.CONTACT_USER) {
      return 3; // Always last
    }
    return 2; // Middle items
  };

  // Sort a new copy of the array (.slice())
  // This prevents modifying the original array, which is the best practice.
  return actions.slice().sort((a, b) => {
    const groupA = getSortGroup(a);
    const groupB = getSortGroup(b);

    // Sort by main group (1, 2, 3)
    if (groupA !== groupB) {
      return groupA - groupB;
    }

    // Sort by Severity (Descending: Critical -> Low)
    const severityA = getSeverity(a);
    const severityB = getSeverity(b);

    if (severityA !== severityB) {
      return severityB - severityA;
    }

    // Sort Alphabetically by Name (A -> Z)
    return i18n.global
      .t(`tickets.eventTriggers.${a.name}`)
      .localeCompare(i18n.global.t(`tickets.eventTriggers.${b.name}`));
  });
};

/**
 * Creates a download link for a device version item.
 * @param item - The device version item
 * @param downloadClientPageLink - The download client page link from the store
 * @returns Promise resolving to the download URL
 */
export async function createDownloadLink(
  item: DeviceVersionItem,
  downloadClientPageLink: string
): Promise<string> {
  const url: { [key in "win" | "macos" | "linux"]: string } = await getDownloadLink({
    id: downloadClientPageLink.split("/")[4],
    os: getConvertedOSTypeForAWS(item.osType),
    version: item.version,
  });
  const convertedOsType = getConvertedOSTypeForAWS(item.osType);
  return url[convertedOsType];
}

/**
 * Handles the download action by creating a temporary link and triggering the download.
 * @param downloadLink - The download link URL
 */
export async function handleDownloadAction(downloadLink: string): Promise<void> {
  const linkEl = document.createElement("a");
  linkEl.href = downloadLink;
  document.body.appendChild(linkEl);
  linkEl.click();
  document.body.removeChild(linkEl);
}

export const handleNavigationInSetupHub = (item: SetupHubStepItem) => {
  switch (item.step) {
    case SetupHubStep.INVITE_TEAMMATES:
      router.push({ name: RouteName.ACCESS_CONTROL_ADMIN_USERS_TAB });
      break;
    case SetupHubStep.CONNECT_CLOUD_APP:
      router.push({ name: RouteName.CLOUD_APPS_PAGE });
      break;
    case SetupHubStep.ADD_PROTECTED_USERS:
      router.push({ name: RouteName.USERS_SETTINGS_USERS_TAB });
      break;
    case SetupHubStep.SETUP_ACCESS_PERMISSIONS:
      router.push({ name: RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB });
      break;
    case SetupHubStep.DEPLOY_ENDPOINT_AGENT:
      router.push({ name: RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB });
      break;
    case SetupHubStep.ACTIVATE_SAT:
      router.push({ name: RouteName.SECURITY_AWARENESS_SETTINGS });
      break;
    case SetupHubStep.ADD_DEVICE_LABELS:
      router.push({ name: RouteName.DEVICES_SETTINGS_LABELS_TAB });
      break;
    case SetupHubStep.ADD_DEVICE_POSTURE_SETTINGS:
      router.push({ name: RouteName.ENDPOINT_SECURITY_DEVICE_POSTURE_TAB });
      break;
    case SetupHubStep.DEFINE_DEVICE_DATA_MONITORING_RULES:
      router.push({ name: RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB });
      break;
    case SetupHubStep.DEFINE_USER_DATA_MONITORING_RULES:
      router.push({ name: RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB });
      break;
    case SetupHubStep.CREATE_SWG_VIRTUAL_SERVER:
      router.push({ name: RouteName.NETWORK_SETTINGS_TAB });
      break;
    case SetupHubStep.CREATE_NETWORK_VIRTUAL_SERVER:
      if (isModuleDisabled(SubscriptionModule.NETWORK)) {
        router.push({ name: RouteName.SECURE_WEB_GATEWAY_SETTINGS });
        return;
      }
      router.push({ name: RouteName.NETWORK_SETTINGS_TAB });
      break;
    case SetupHubStep.SETUP_VPN_ACCESS:
      router.push({ name: RouteName.NETWORK_VPN_TAB });
      break;
    case SetupHubStep.ENABLE_DNS_FILTERING:
      router.push({ name: RouteName.SECURE_WEB_GATEWAY_SETTINGS });
      break;
  }
};

export function serviceHasMissingRequiredPermissions(
  service: CloudAppService,
  routeName: RouteName
) {
  return service.permissions.some((permission) => {
    if (permission.requiredInContext?.includes(getCloudSecurityContext(routeName))) {
      return !permission.granted;
    }
  });
}
